import {
  ProfileManagementDialog,
  ProfileVerificationCodeDialog,
  GenSnackBar,
} from '@pinnacleautomotive/clarity-components';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { amplifyApi } from '../../hooks/amplifyApi';
import {
  clearSaveUserProfileStatus,
  clearVerificationCodeResent,
  closeProfileManagerDialog,
  openProfileManagerDialog,
  resendVerificationCode,
  saveUserProfile,
  sendVerificationCode,
  verifyCode,
} from '../../redux/actions/userManagementActions';
import {
  selectIsSavingProfile,
  selectPersonId,
  selectProfileManagerDialogOpen,
  selectSaveProfileStatus,
  selectUserPerson,
  selectVerificationCodeResent,
  selectVerificationLoading,
  selectVerificationStatus,
  selectVerificationModalOpen,
} from '../../redux/selectors/userDataSelectors';

export default function AppProfileManager() {
  const { t } = useTranslation('global');
  const dispatch = useDispatch();
  const open = useSelector(selectProfileManagerDialogOpen);
  const person = useSelector(selectUserPerson);
  const isLoading = useSelector(selectIsSavingProfile);
  const personId = useSelector(selectPersonId);
  const saveProfileStatus = useSelector(selectSaveProfileStatus);
  const verificationCodeResent = useSelector(selectVerificationCodeResent);
  const verificationStatus = useSelector(selectVerificationStatus);
  const verificationLoading = useSelector(selectVerificationLoading);
  const verificationModalOpen = useSelector(selectVerificationModalOpen);

  const closeDialog = () => {
    dispatch(closeProfileManagerDialog());
  };

  const saveProfile = newData => {
    const payload = { ...newData };
    payload.photo === person.photo && delete payload.photo;
    dispatch(saveUserProfile({ personId, data: payload }));
  };

  const clearSaveStatus = () => {
    dispatch(clearSaveUserProfileStatus());
  };

  const closeResendVerificationCodeSnackBar = () => {
    dispatch(clearVerificationCodeResent());
  };

  useEffect(() => {
    if (saveProfileStatus !== 'success' || !verificationModalOpen) return;
    dispatch(sendVerificationCode({ personId }));
  }, [dispatch, personId, saveProfileStatus, verificationModalOpen]);

  const resendVerificationCodeHandler = () => {
    dispatch(resendVerificationCode({ personId }));
  };

  const verifyCodeHandler = code => {
    dispatch(verifyCode({ personId, code }));
  };

  const successSnackBarOpen =
    saveProfileStatus === 'success' &&
    !verificationModalOpen &&
    !verificationStatus;

  const errorSnackBarOpen = saveProfileStatus === 'error';

  return (
    <>
      <GenSnackBar
        open={verificationStatus === 'success'}
        title={t('profileManager.verificationSuccess')}
        onClose={clearSaveStatus}
      />
      <GenSnackBar
        open={successSnackBarOpen}
        title={t('profileManager.success')}
        onClose={clearSaveStatus}
      />
      <GenSnackBar
        open={errorSnackBarOpen}
        title={t('profileManager.error')}
        type="error"
        onClose={clearSaveStatus}
      />
      <GenSnackBar
        message={'A new verification code has been sent by SMS'}
        open={verificationCodeResent}
        icon={false}
        color={'#323232'}
        onClose={closeResendVerificationCodeSnackBar}
      />
      <ProfileVerificationCodeDialog
        open={verificationModalOpen}
        isLoading={verificationLoading}
        error={
          verificationStatus === 'error' && t('profileManager.invalidCode')
        }
        phone={person.phone}
        onGoBack={() => dispatch(openProfileManagerDialog())}
        onResend={resendVerificationCodeHandler}
        onVerify={verifyCodeHandler}
      />
      <ProfileManagementDialog
        key={saveProfileStatus}
        amplifyApi={amplifyApi}
        open={open}
        isLoading={isLoading}
        data={person}
        onClose={closeDialog}
        onSave={saveProfile}
      />
    </>
  );
}
